import React, { useState, useRef, useEffect } from 'react';
import { SearchAutocomplete } from './SearchAutocomplete';

interface FilterState {
  minPrice: string;
  maxPrice: string;
  beds: string;
  baths: string;
  sortBy: string;
  sortOrder: string;
  moveinDate: string;
}

interface CityFilterBarProps {
  title: string;
  propertyCount: number;
  filters: FilterState;
  onFilterChange: (filters: Partial<FilterState>) => void;
}

const PRICE_OPTIONS = ['any', '600', '700', '800', '900', '1000', '1500', '2000'];

const ChevronDown = () => (
  <svg viewBox="0 0 10 6" width="10" height="6" fill="currentColor">
    <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
  </svg>
);

const SortIcon = () => (
  <svg viewBox="0 0 320 512" width="14" height="14" fill="currentColor">
    <path d="M137.4 41.4c12.5-12.5 32.8-12.5 45.3 0l128 128c9.2 9.2 11.9 22.9 6.9 34.9S302.7 224 288 224H32c-14.7 0-28-8.6-33-22s-2.3-28.8 6.9-38l128-128zm0 429.3l-128-128c-9.2-9.2-11.9-22.9-6.9-34.9S17.3 288 32 288h256c14.7 0 28 8.6 33 22s2.3 28.8-6.9 38l-128 128c-12.5 12.5-32.8 12.5-45.3 0z" />
  </svg>
);

const FilterIcon = () => (
  <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor">
    <path d="M0 73.7C0 50.7 18.7 32 41.7 32H470.3c23 0 41.7 18.7 41.7 41.7c0 9.6-3.3 18.9-9.4 26.3L336 304.5V447.7c0 17.8-14.5 32.3-32.3 32.3c-7.3 0-14.4-2.5-20.1-7l-92.1-73.4c-9.6-7.6-15.2-19.2-15.2-31.5V304.5L9.4 100C3.3 92.6 0 83.3 0 73.7z" />
  </svg>
);

function FilterDropdown({ label, children, isOpen, onToggle }: { label: string; children: React.ReactNode; isOpen: boolean; onToggle: () => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onToggle]);

  return (
    <div className="case-filter-dropdown" ref={ref}>
      <button className={`case-filter-dropdown-btn${isOpen ? ' open' : ''}`} onClick={onToggle} type="button">
        {label} <ChevronDown />
      </button>
      <div className={`case-filter-dropdown-panel${isOpen ? ' open' : ''}`}>
        {children}
      </div>
    </div>
  );
}

export function CityFilterBar({ title, propertyCount, filters, onFilterChange }: CityFilterBarProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sortOpen) return;
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [sortOpen]);

  const toggle = (key: string) => setOpenDropdown(prev => prev === key ? null : key);

  const renderPriceRadios = (name: 'minPrice' | 'maxPrice', label: string) => (
    <div>
      <div className="case-filter-group-title">{label}</div>
      <div className="case-filter-radios">
        {PRICE_OPTIONS.map(v => (
          <label key={v} className="case-filter-radio">
            <input
              type="radio"
              name={name}
              value={v}
              checked={filters[name] === v}
              onChange={() => onFilterChange({ [name]: v })}
            />
            {v === 'any' ? 'Any' : `$${Number(v).toLocaleString()}`}
          </label>
        ))}
      </div>
    </div>
  );

  const renderBedsRadios = () => (
    <div>
      <div className="case-filter-group-title">Bedrooms</div>
      <div className="case-filter-radios">
        {['any', '1', '2', '3'].map(v => (
          <label key={v} className="case-filter-radio">
            <input type="radio" name="beds" value={v} checked={filters.beds === v} onChange={() => onFilterChange({ beds: v })} />
            {v === 'any' ? 'Any' : `${v} Bed`}
          </label>
        ))}
      </div>
    </div>
  );

  const renderBathsRadios = () => (
    <div>
      <div className="case-filter-group-title">Baths</div>
      <div className="case-filter-radios">
        {['any', '1', '2', '3'].map(v => (
          <label key={v} className="case-filter-radio">
            <input type="radio" name="baths" value={v} checked={filters.baths === v} onChange={() => onFilterChange({ baths: v })} />
            {v === 'any' ? 'Any' : `${v} Bath`}
          </label>
        ))}
      </div>
    </div>
  );

  const renderDateFilter = () => (
    <div>
      <div className="case-filter-group-title">Move-in Date</div>
      <input
        type="date"
        value={filters.moveinDate === 'any' ? '' : filters.moveinDate}
        onChange={(e) => onFilterChange({ moveinDate: e.target.value || 'any' })}
        min={new Date().toISOString().split('T')[0]}
        style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--case-border)', borderRadius: '3px', fontFamily: 'inherit', fontSize: '14px' }}
      />
      {filters.moveinDate !== 'any' && (
        <button type="button" onClick={() => onFilterChange({ moveinDate: 'any' })} style={{ marginTop: '8px', fontSize: '13px', color: 'var(--case-primary)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
          Reset
        </button>
      )}
    </div>
  );

  return (
    <>
      <section className="case-filter-section">
        <div className="case-filter-row">
          <div className="case-filter-left">
            <h1 className="case-filter-title">
              <span>{propertyCount}</span> {title}
            </h1>
            <div style={{ position: 'relative' }} ref={sortRef}>
              <button className="case-sort-btn" type="button" onClick={() => setSortOpen(!sortOpen)}>
                Sort <SortIcon />
              </button>
              <div className={`case-sort-panel${sortOpen ? ' open' : ''}`}>
                <div className="case-filter-group-title">Order By</div>
                <div className="case-filter-radios" style={{ gridTemplateColumns: '1fr', marginBottom: '15px' }}>
                  {['asc', 'desc'].map(v => (
                    <label key={v} className="case-filter-radio">
                      <input type="radio" name="sortOrder" value={v} checked={filters.sortOrder === v} onChange={() => onFilterChange({ sortOrder: v })} />
                      {v === 'asc' ? 'Ascending' : 'Descending'}
                    </label>
                  ))}
                </div>
                <div className="case-filter-group-title">Sort By</div>
                <div className="case-filter-radios" style={{ gridTemplateColumns: '1fr' }}>
                  {[{ v: 'price', l: 'Price' }, { v: 'alphabetically', l: 'A-Z' }].map(({ v, l }) => (
                    <label key={v} className="case-filter-radio">
                      <input type="radio" name="sortBy" value={v} checked={filters.sortBy === v} onChange={() => onFilterChange({ sortBy: v })} />
                      {l}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="case-filter-right">
            <div className="case-market-search">
              <SearchAutocomplete variant="inline" placeholder="Search by Community, City, State or Zip" />
            </div>

            <button className="case-mobile-filter-btn" type="button" onClick={() => setMobileOpen(true)}>
              <FilterIcon /> Filters
            </button>

            <FilterDropdown label="Price" isOpen={openDropdown === 'price'} onToggle={() => toggle('price')}>
              {renderPriceRadios('minPrice', 'Min Rent')}
              <div style={{ marginTop: '15px' }} />
              {renderPriceRadios('maxPrice', 'Max Rent')}
            </FilterDropdown>

            <FilterDropdown label="Bedrooms" isOpen={openDropdown === 'beds'} onToggle={() => toggle('beds')}>
              {renderBedsRadios()}
            </FilterDropdown>

            <FilterDropdown label="Move-in Date" isOpen={openDropdown === 'date'} onToggle={() => toggle('date')}>
              {renderDateFilter()}
            </FilterDropdown>

            <FilterDropdown label="More" isOpen={openDropdown === 'more'} onToggle={() => toggle('more')}>
              {renderBathsRadios()}
            </FilterDropdown>
          </div>
        </div>
      </section>

      {/* Mobile filter panel */}
      <div className={`case-mobile-filter-panel${mobileOpen ? ' open' : ''}`}>
        <button className="case-mobile-filter-close" type="button" onClick={() => setMobileOpen(false)}>&times;</button>
        <div className="case-mobile-filter-section">
          <div className="case-mobile-filter-section-title">Price</div>
          {renderPriceRadios('minPrice', 'Min Rent')}
          <div style={{ marginTop: '15px' }} />
          {renderPriceRadios('maxPrice', 'Max Rent')}
        </div>
        <div className="case-mobile-filter-section">
          <div className="case-mobile-filter-section-title">Bedrooms</div>
          {renderBedsRadios()}
        </div>
        <div className="case-mobile-filter-section">
          <div className="case-mobile-filter-section-title">Baths</div>
          {renderBathsRadios()}
        </div>
        <div className="case-mobile-filter-section">
          <div className="case-mobile-filter-section-title">Move-in Date</div>
          {renderDateFilter()}
        </div>
      </div>
      {mobileOpen && <div className="case-overlay open" onClick={() => setMobileOpen(false)} />}
    </>
  );
}

export type { FilterState };
export default CityFilterBar;
